import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import {
    Loader2, Globe, Instagram, Facebook, Linkedin, MapPinned, RefreshCw,
    ExternalLink, Sparkles, CheckCircle2, XCircle, AlertTriangle, HelpCircle,
    ChevronDown, ChevronUp, Star, Radar, Lightbulb, Scale, Edit3, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AnalysisProgress from "@/components/AnalysisProgress";
import { toast } from "sonner";

const PLATFORMS = ["google_business", "website", "instagram", "facebook", "linkedin"];
const META = {
    website: { label: "Website", icon: Globe },
    google_business: { label: "Google Business Profile", icon: MapPinned },
    instagram: { label: "Instagram", icon: Instagram },
    facebook: { label: "Facebook", icon: Facebook },
    linkedin: { label: "LinkedIn", icon: Linkedin },
};

const num = (v) => (typeof v === "number" ? v.toLocaleString() : v);
const TILES = {
    website: [
        ["load_ms", "Response", (v) => `${(v / 1000).toFixed(1)} s`],
        ["word_count", "Words", num],
        ["schema_types", "Schema", (v) => (v?.length ? v.slice(0, 2).join(", ") : "None")],
        ["http_status", "HTTP", (v) => v],
    ],
    google_business: [
        ["rating", "Rating", (v) => `${v} ★`],
        ["review_count", "Reviews", num],
        ["photo_count", "Photos", (v) => (v >= 10 ? "10+" : v)],
        ["category", "Category", (v) => v],
    ],
    instagram: [
        ["followers", "Followers", num],
        ["posts", "Posts", num],
        ["engagement_rate_pct", "Engagement", (v) => `${v}%`],
        ["days_since_last_post", "Last post", (v) => `${v} d ago`],
        ["posts_last_30_days", "Posts · 30 d", num],
    ],
    facebook: [
        ["followers", "Followers", num],
        ["likes", "Likes", num],
        ["talking_about", "Talking about", num],
        ["were_here", "Check-ins", num],
    ],
    linkedin: [
        ["followers", "Followers", num],
        ["employees_on_linkedin", "Employees", num],
    ],
};

const STATUS_BADGE = {
    analysed: ["Analysed", "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"],
    limited: ["Limited data", "border-amber-500/40 bg-amber-500/10 text-amber-200"],
    not_provided: ["Not found", "border-rose-500/40 bg-rose-500/10 text-rose-200"],
    error: ["Error", "border-rose-500/40 bg-rose-500/10 text-rose-200"],
};

const CHECK_ICON = {
    pass: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />,
    warn: <AlertTriangle className="h-3.5 w-3.5 text-amber-300" />,
    fail: <XCircle className="h-3.5 w-3.5 text-rose-300" />,
    unknown: <HelpCircle className="h-3.5 w-3.5 text-slate-500" />,
};
const CHECK_ORDER = { fail: 0, warn: 1, pass: 2, unknown: 3 };

const PILL = {
    high: "bg-rose-500/10 text-rose-200 border-rose-500/30",
    medium: "bg-amber-500/10 text-amber-200 border-amber-500/30",
    low: "bg-slate-500/10 text-slate-300 border-slate-500/30",
};

function scoreColor(s) {
    if (s == null) return "text-slate-500";
    return s >= 75 ? "text-emerald-300" : s >= 45 ? "text-amber-300" : "text-rose-300";
}
function scoreStroke(s) {
    if (s == null) return "#475569";
    return s >= 75 ? "#6ee7b7" : s >= 45 ? "#fcd34d" : "#fda4af";
}

function ScoreRing({ score, size = 64, stroke = 6, testid }) {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const pct = score == null ? 0 : Math.max(0, Math.min(100, score)) / 100;
    return (
        <div className="relative shrink-0" style={{ width: size, height: size }} data-testid={testid}>
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={scoreStroke(score)} strokeWidth={stroke}
                    strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round" />
            </svg>
            <div className={`absolute inset-0 flex items-center justify-center font-extrabold ${scoreColor(score)}`}
                style={{ fontSize: size * 0.3 }}>
                {score == null ? "—" : score}
            </div>
        </div>
    );
}

function Pill({ kind, children }) {
    return <span className={`inline-flex rounded border px-1.5 py-px text-[10px] font-semibold ${PILL[kind] || PILL.low}`}>{children}</span>;
}

export default function Presence() {
    const { currentBusiness } = useApp();
    const nav = useNavigate();
    const [analysis, setAnalysis] = useState(null);
    const [providers, setProviders] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const pollRef = useRef(null);

    const stopPoll = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
    };

    const poll = useCallback((bizId, aid) => {
        stopPoll();
        pollRef.current = setInterval(async () => {
            try {
                const r = await api.presenceGet(bizId, aid);
                setAnalysis(r.analysis);
                const allSteps = (r.analysis.steps || []).every((s) => ["done", "failed"].includes(s.status));
                if (r.analysis.status === "failed" || (r.analysis.status === "done" && allSteps)) stopPoll();
            } catch {
                /* keep polling */
            }
        }, 2500);
    }, []);

    useEffect(() => {
        if (!currentBusiness) return undefined;
        let alive = true;
        setLoading(true);
        setAnalysis(null);
        stopPoll();
        api.presenceLatest(currentBusiness.id)
            .then((r) => {
                if (!alive) return;
                setAnalysis(r.analysis);
                setProviders(r.providers);
                if (r.analysis?.status === "running") poll(currentBusiness.id, r.analysis.id);
            })
            .catch(() => toast.error("Couldn't load the analysis."))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
            stopPoll();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBusiness?.id, poll]);

    const runAnalysis = async () => {
        setStarting(true);
        try {
            const r = await api.presenceAnalyse(currentBusiness.id);
            setAnalysis(r.analysis);
            poll(currentBusiness.id, r.analysis.id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't start the analysis.");
        } finally {
            setStarting(false);
        }
    };

    if (!currentBusiness || loading) {
        return <div className="max-w-6xl mx-auto py-16 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>;
    }

    const running = analysis?.status === "running";
    const done = analysis?.status === "done";
    const ai = analysis?.ai || {};
    const platforms = analysis?.platforms || {};

    return (
        <div className="max-w-6xl mx-auto space-y-6 fade-in" data-testid="presence-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow text-cyan-300">Presence Analysis</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-slate-50">{currentBusiness.name}</h1>
                    <p className="mt-2 text-sm text-slate-400">
                        Each approved profile is analysed from live data, then AI turns the findings into a plan.
                        {analysis?.finished_at && <> · Last run {new Date(analysis.finished_at).toLocaleString()}</>}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => nav("/company-profile")} className="border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-200 gap-2" data-testid="presence-edit-handles">
                        <Edit3 className="h-4 w-4" /> Edit profiles
                    </Button>
                    <Button onClick={runAnalysis} disabled={starting || running} className="bg-indigo-500 hover:bg-indigo-400 text-white gap-2" data-testid="presence-run-btn">
                        {starting || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        {analysis ? "Re-run analysis" : "Run analysis"}
                    </Button>
                </div>
            </div>

            {(providers?.warnings || []).length > 0 && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-xs text-amber-100 space-y-1">
                    <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Some data sources are not configured</div>
                    {providers.warnings.map((w, i) => <div key={i} className="text-amber-100/80">{w}</div>)}
                </div>
            )}

            {!analysis && (
                <div className="surface rounded-2xl p-10 text-center" data-testid="presence-empty">
                    <Radar className="h-8 w-8 mx-auto text-indigo-300" />
                    <div className="mt-3 text-lg font-bold text-slate-100">No analysis yet</div>
                    <p className="mt-1 text-sm text-slate-400">Analyse the website, Google Business Profile and social profiles saved for this business.</p>
                    <Button onClick={runAnalysis} disabled={starting} className="mt-5 bg-indigo-500 hover:bg-indigo-400 text-white gap-2">
                        {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Analyse my online presence
                    </Button>
                </div>
            )}

            {(running || analysis?.status === "failed") && (
                <AnalysisProgress analysis={analysis} onOpen={runAnalysis} openLabel="Try again" onHome={() => nav("/dashboard")} />
            )}

            {done && (
                <>
                    {/* Overview */}
                    <div className="grid lg:grid-cols-[280px_1fr] gap-4">
                        <div className="surface rounded-2xl p-5 flex items-center gap-5" data-testid="presence-overall">
                            <ScoreRing score={analysis.overall_score} size={96} stroke={8} testid="presence-overall-score" />
                            <div>
                                <div className="label-eyebrow">Overall presence</div>
                                <div className="mt-1 text-sm text-slate-200">
                                    {analysis.coverage?.found ?? 0} of {analysis.coverage?.total ?? 5} profiles found
                                </div>
                                {ai.overall_assessment && (
                                    <div className={`mt-1 text-xs font-semibold uppercase tracking-wide ${{ strong: "text-emerald-300", fair: "text-amber-300", weak: "text-rose-300" }[ai.overall_assessment]}`}>
                                        {ai.overall_assessment}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="surface rounded-2xl p-5" data-testid="presence-ai-summary">
                            <div className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-indigo-300" />
                                <span className="label-eyebrow text-indigo-300">
                                    {analysis.ai_source === "llm" ? "AI analysis" : "Automated analysis (AI unavailable)"}
                                </span>
                            </div>
                            {ai.headline && <div className="mt-2 text-lg font-bold text-slate-50">{ai.headline}</div>}
                            {ai.summary && <p className="mt-2 text-sm leading-relaxed text-slate-300">{ai.summary}</p>}
                        </div>
                    </div>

                    {/* Priority actions */}
                    {(ai.priority_actions || []).length > 0 && (
                        <div className="surface rounded-2xl p-5" data-testid="presence-priority-actions">
                            <div className="text-sm font-bold text-slate-100">Do these first</div>
                            <ol className="mt-3 space-y-3">
                                {ai.priority_actions.map((a, i) => (
                                    <li key={i} className="flex gap-3">
                                        <span className="h-6 w-6 shrink-0 rounded-full bg-indigo-500/15 border border-indigo-500/30 text-indigo-200 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                                        <div className="min-w-0">
                                            <div className="text-sm text-slate-100">{a.action}</div>
                                            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400">
                                                {a.platform && META[a.platform] && <span className="text-slate-300">{META[a.platform].label}</span>}
                                                {a.impact && <Pill kind={a.impact}>{a.impact} impact</Pill>}
                                                {a.effort && <Pill kind="low">{a.effort} effort</Pill>}
                                                {a.why && <span>· {a.why}</span>}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {/* Platforms */}
                    <div className="grid lg:grid-cols-2 gap-4">
                        {PLATFORMS.map((p) => (
                            <PlatformCard key={p} platform={p} r={platforms[p]} ai={(ai.platforms || {})[p]} onEdit={() => nav("/company-profile")} />
                        ))}
                        <ConsistencyCard c={analysis.consistency} findings={ai.consistency_findings} />
                    </div>

                    {/* Reviews */}
                    <ReviewsCard gbp={platforms.google_business} insights={ai.review_insights} />

                    {/* Ideas + gaps */}
                    {((ai.content_ideas || []).length > 0 || (ai.data_gaps || []).length > 0) && (
                        <div className="grid lg:grid-cols-2 gap-4">
                            {(ai.content_ideas || []).length > 0 && (
                                <div className="surface rounded-2xl p-5">
                                    <div className="flex items-center gap-2 text-sm font-bold text-slate-100"><Lightbulb className="h-4 w-4 text-amber-300" /> Content ideas</div>
                                    <ul className="mt-3 space-y-2 text-sm text-slate-300 list-disc pl-5">
                                        {ai.content_ideas.map((x, i) => <li key={i}>{x}</li>)}
                                    </ul>
                                </div>
                            )}
                            {(ai.data_gaps || []).length > 0 && (
                                <div className="surface rounded-2xl p-5">
                                    <div className="flex items-center gap-2 text-sm font-bold text-slate-100"><Info className="h-4 w-4 text-slate-400" /> What we couldn't see</div>
                                    <ul className="mt-3 space-y-2 text-sm text-slate-400 list-disc pl-5">
                                        {ai.data_gaps.map((x, i) => <li key={i}>{x}</li>)}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function PlatformCard({ platform, r, ai, onEdit }) {
    const [showAll, setShowAll] = useState(false);
    const meta = META[platform];
    const Icon = meta.icon;
    if (!r) return null;
    const [badge, badgeCls] = STATUS_BADGE[r.status] || ["—", "border-white/10 text-slate-400"];
    const tiles = (TILES[platform] || [])
        .filter(([k]) => r.metrics?.[k] !== undefined && r.metrics?.[k] !== null && r.metrics?.[k] !== "")
        .slice(0, 4);
    const checks = [...(r.checks || [])].sort((a, b) => CHECK_ORDER[a.status] - CHECK_ORDER[b.status]);
    const visible = showAll ? checks : checks.slice(0, 5);

    return (
        <div className="surface rounded-2xl p-5 flex flex-col" data-testid={`presence-card-${platform}`}>
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/[0.03] border border-white/5 flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-slate-300" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-slate-100">{meta.label}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${badgeCls}`}>{badge}</span>
                    </div>
                    {r.url ? (
                        <a href={r.url} target="_blank" rel="noreferrer" className="mt-0.5 text-xs text-cyan-300 hover:underline inline-flex items-center gap-1 break-all">
                            {r.url} <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                    ) : (
                        <button onClick={onEdit} className="mt-0.5 text-xs text-slate-400 hover:text-slate-200 underline-offset-2 hover:underline">Add it in Company Profile</button>
                    )}
                    <div className="mt-0.5 text-[10px] mono uppercase tracking-wider text-slate-500">Source: {r.source}</div>
                </div>
                <ScoreRing score={r.score} size={52} stroke={5} testid={`presence-score-${platform}`} />
            </div>

            {tiles.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {tiles.map(([k, label, fmt]) => (
                        <div key={k} className="rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-2">
                            <div className="text-[10px] text-slate-500 truncate">{label}</div>
                            <div className="text-sm font-bold text-slate-100 truncate" title={String(fmt(r.metrics[k]))}>{fmt(r.metrics[k])}</div>
                        </div>
                    ))}
                </div>
            )}

            {ai?.verdict && <p className="mt-4 text-sm text-slate-300">{ai.verdict}</p>}

            {(ai?.strengths?.length > 0 || ai?.gaps?.length > 0) && (
                <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs">
                    {ai.strengths?.length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-emerald-300/80 font-semibold">Working</div>
                            <ul className="mt-1 space-y-1 text-slate-300">{ai.strengths.map((s, i) => <li key={i} className="flex gap-1.5"><CheckCircle2 className="h-3 w-3 mt-0.5 text-emerald-400 shrink-0" />{s}</li>)}</ul>
                        </div>
                    )}
                    {ai.gaps?.length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-amber-300/80 font-semibold">Gaps</div>
                            <ul className="mt-1 space-y-1 text-slate-300">{ai.gaps.map((s, i) => <li key={i} className="flex gap-1.5"><AlertTriangle className="h-3 w-3 mt-0.5 text-amber-400 shrink-0" />{s}</li>)}</ul>
                        </div>
                    )}
                </div>
            )}

            {ai?.recommendations?.length > 0 && (
                <div className="mt-4 rounded-xl border border-indigo-500/20 bg-indigo-500/[0.04] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-indigo-300 font-semibold">Recommended</div>
                    <ul className="mt-2 space-y-2">
                        {ai.recommendations.map((x, i) => (
                            <li key={i} className="text-xs text-slate-200">
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span>{x.action}</span>
                                    {x.impact && <Pill kind={x.impact}>{x.impact}</Pill>}
                                </div>
                                {x.why && <div className="text-[11px] text-slate-500 mt-0.5">{x.why}</div>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {checks.length > 0 && (
                <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Checks</div>
                    <ul className="mt-1 divide-y divide-white/5">
                        {visible.map((c) => (
                            <li key={c.id} className="py-1.5 flex items-start gap-2" data-testid={`check-${platform}-${c.id}`}>
                                <span className="mt-0.5">{CHECK_ICON[c.status]}</span>
                                <div className="min-w-0">
                                    <div className="text-xs text-slate-200">{c.label}</div>
                                    {c.detail && <div className="text-[11px] text-slate-500 break-words">{c.detail}</div>}
                                </div>
                            </li>
                        ))}
                    </ul>
                    {checks.length > 5 && (
                        <button onClick={() => setShowAll((s) => !s)} className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-200">
                            {showAll ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            {showAll ? "Show fewer" : `Show all ${checks.length} checks`}
                        </button>
                    )}
                </div>
            )}

            {(r.notes || []).length > 0 && (
                <div className="mt-3 space-y-1">
                    {r.notes.map((n, i) => (
                        <div key={i} className="text-[11px] text-amber-200/80 flex gap-1.5"><Info className="h-3 w-3 mt-0.5 shrink-0" />{n}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ConsistencyCard({ c, findings }) {
    if (!c) return null;
    return (
        <div className="surface rounded-2xl p-5" data-testid="presence-consistency">
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/[0.03] border border-white/5 flex items-center justify-center shrink-0">
                    <Scale className="h-4 w-4 text-slate-300" />
                </div>
                <div className="flex-1">
                    <div className="text-sm font-bold text-slate-100">Name, phone & address consistency</div>
                    <div className="text-[11px] text-slate-500">Google ranks local businesses higher when these match everywhere.</div>
                </div>
                <ScoreRing score={c.score} size={52} stroke={5} />
            </div>
            <div className="mt-4 overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-left text-slate-500">
                            <th className="py-1 pr-3 font-medium">Source</th>
                            <th className="py-1 pr-3 font-medium">Phone</th>
                            <th className="py-1 font-medium">Address</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {(c.rows || []).map((row, i) => (
                            <tr key={i} className="align-top">
                                <td className="py-1.5 pr-3 text-slate-300 whitespace-nowrap">{row.source}</td>
                                <td className="py-1.5 pr-3 text-slate-400">{row.phone || "—"}</td>
                                <td className="py-1.5 text-slate-400">{row.address || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <ul className="mt-3 space-y-1">
                {(c.checks || []).map((k) => (
                    <li key={k.id} className="flex items-start gap-2 text-xs">
                        <span className="mt-0.5">{CHECK_ICON[k.status]}</span>
                        <span className="text-slate-200">{k.label}{k.detail && <span className="text-slate-500"> — {k.detail}</span>}</span>
                    </li>
                ))}
            </ul>
            {(findings || []).length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-slate-300 list-disc pl-5">
                    {findings.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
            )}
        </div>
    );
}

function ReviewsCard({ gbp, insights }) {
    const reviews = gbp?.metrics?.recent_reviews || [];
    const hasInsights = insights && ((insights.positive_themes || []).length || (insights.negative_themes || []).length || insights.reply_tip);
    if (!reviews.length && !hasInsights) return null;
    return (
        <div className="surface rounded-2xl p-5" data-testid="presence-reviews">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-100"><Star className="h-4 w-4 text-amber-300" /> Google reviews</div>
            {hasInsights && (
                <div className="mt-3 grid sm:grid-cols-2 gap-4 text-xs">
                    {(insights.positive_themes || []).length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-emerald-300/80 font-semibold">Customers praise</div>
                            <ul className="mt-1 space-y-1 text-slate-300 list-disc pl-4">{insights.positive_themes.map((t, i) => <li key={i}>{t}</li>)}</ul>
                        </div>
                    )}
                    {(insights.negative_themes || []).length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-rose-300/80 font-semibold">Customers complain about</div>
                            <ul className="mt-1 space-y-1 text-slate-300 list-disc pl-4">{insights.negative_themes.map((t, i) => <li key={i}>{t}</li>)}</ul>
                        </div>
                    )}
                    {insights.reply_tip && <div className="sm:col-span-2 text-slate-400"><b className="text-slate-200">Reply tip:</b> {insights.reply_tip}</div>}
                </div>
            )}
            {reviews.length > 0 && (
                <div className="mt-4 grid md:grid-cols-2 gap-2">
                    {reviews.map((rv, i) => (
                        <div key={i} className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
                            <div className="flex items-center justify-between text-[11px]">
                                <span className="text-amber-200">{"★".repeat(rv.rating || 0)}<span className="text-slate-600">{"★".repeat(5 - (rv.rating || 0))}</span></span>
                                <span className="text-slate-500">{rv.author} · {rv.relative_time}</span>
                            </div>
                            <p className="mt-1.5 text-xs text-slate-300 line-clamp-4">{rv.text || <i className="text-slate-500">No text</i>}</p>
                        </div>
                    ))}
                </div>
            )}
            <div className="mt-2 text-[10px] text-slate-500">Google shows up to 5 reviews through its API.</div>
        </div>
    );
}
